import React, { useEffect, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router";
import {
  Alert,
  Button,
  Checkbox,
  Link,
  TextField,
  Typography,
} from "@platform/design-system";
import { LogoWhite } from "@platform/portal-ui";

import { isValidEmail } from "../../utils";
import useAuth from "../../hooks/useAuth";
import useMfa from "../../auth/useMfa";
import MfaChallenge from "../../components/mfaChallenge";
import { LoginPage, LOGIN_PAGE_TEST_ID } from "../../components/loginPage";
import {
  ROUTE_NAMES,
  STATUS_CODE,
  WRONG_CREDENTIALS_CODE_B2C,
  WRONG_CREDENTIALS_TEXT_B2C,
} from "../../constants";
import { submitEvent } from "../../utils/tracking";
import envVariables from "../../projectEnvVariables";

const EMAIL = "email";
const PASSWORD = "password";
const KEEP_SESSION_ACTIVE = "keepSessionActive";

const Login = () => {
  const [state, setState] = useState({
    [EMAIL]: "",
    [PASSWORD]: "",
    [KEEP_SESSION_ACTIVE]: true,
    apiError: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { logIn, sessionEndMessage } = useAuth();
  const [mfaChallenge, setMfaChallenge] = useState(false);
  const { sendMfaCode } = useMfa();

  useEffect(() => {
    if (sessionEndMessage) {
      setState((prevState) => ({
        ...prevState,
        apiError: sessionEndMessage,
      }));
    }
  }, [sessionEndMessage]);
  const isValidInfo =
    !Object.values(errors).some((e) => e !== null) &&
    !!state[PASSWORD] &&
    !!state[EMAIL] &&
    isValidEmail(state[EMAIL]);

  const handleMfaFlow = (result) => {
    if (result.enabled) {
      sendMfaCode(result.userId);
      setMfaChallenge(true);
    } else if (result.enforce) {
      // MFA is required but not set up, and enrollment can no longer be
      // triggered from the frontend — surface it instead of a stuck spinner.
      setLoading(false);
      setState((prevState) => ({
        ...prevState,
        apiError:
          "Multi-factor authentication is required for your account but has not been set up. Please contact support.",
      }));
    }
  };

  const handleLoginError = (response) => {
    const errorMessage = response?.data?.errorMessage || "";
    const errorCode = errorMessage?.split(":")[0] || response?.status || "";
    const regex = /(\s+)(.*)(\.)/;
    setState((prevState) => ({
      ...prevState,
      apiError:
        errorCode === WRONG_CREDENTIALS_CODE_B2C ||
        errorCode === STATUS_CODE.UNAUTHORIZED
          ? WRONG_CREDENTIALS_TEXT_B2C
          : errorMessage.match(regex)?.[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState((prevState) => ({ ...prevState, apiError: "" }));
    try {
      setLoading(true);
      const result = await logIn(
        state[EMAIL],
        state[PASSWORD],
        state[KEEP_SESSION_ACTIVE],
      );
      if (result?.status === STATUS_CODE.UNAUTHORIZED) {
        setLoading(false);
        setState((prevState) => ({
          ...prevState,
          apiError: WRONG_CREDENTIALS_TEXT_B2C,
        }));
        return;
      }

      // Handle SSO redirect for @centrobenefits.com users
      if (result?.redirect && result?.path) {
        setLoading(false);
        navigate(result.path);
        return;
      }

      handleMfaFlow(result);
    } catch ({ response }) {
      setLoading(false);
      handleLoginError(response);
    }
  };

  const handleBlur = ({ target }) => {
    const { name, value } = target;
    if (!value) {
      setErrors({ ...errors, [name]: "Please complete the field." });
    }

    if (name === EMAIL && !isValidEmail(value)) {
      setErrors({ ...errors, [name]: "Invalid email." });
    }
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setState({ ...state, [name]: value });
    setErrors({ ...errors, [name]: null });
  };

  const handleCheckboxChange = ({ name, value }) => {
    setState({ ...state, [name]: value });
    setErrors({ ...errors, [name]: null });
  };

  const handleForgotPassword = async () => {
    submitEvent("Reset Password Clicked");
    navigate(ROUTE_NAMES.RESET_PASSWORD);
  };

  return (
    <LoginPage
      container
      data-testid={LOGIN_PAGE_TEST_ID}
      justifyContent="space-between"
    >
      <Grid size={3} margin={"auto 6rem"}>
        {mfaChallenge ? (
          <>
            <Typography variant="display-large">Verification</Typography>
            <Box display="flex" flexDirection="column" gap={2} mb={2} mt={4}>
              <MfaChallenge />
            </Box>
          </>
        ) : (
          <>
            <Typography variant="display-medium">Sign in</Typography>
            {state.apiError && (
              <Box mt={2} mb={2}>
                <Alert severity="error">{state.apiError}</Alert>
              </Box>
            )}
            <Box
              component="form"
              display="flex"
              flexDirection="column"
              gap={2}
              onSubmit={handleSubmit}
              mb={2}
              mt={4}
            >
              <TextField
                fullWidth
                value={state[EMAIL]}
                onChange={handleChange}
                onBlur={handleBlur}
                name={EMAIL}
                error={!!errors[EMAIL]}
                helperText={errors[EMAIL]}
                label="Email address"
              />
              <TextField
                label="Password"
                fullWidth
                type={showPassword ? "text" : "password"}
                value={state[PASSWORD]}
                onChange={handleChange}
                onBlur={handleBlur}
                name={PASSWORD}
                autoComplete="none"
                error={!!errors[PASSWORD]}
                helperText={errors[PASSWORD]}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControlLabel
                label="Keep me signed in"
                checked={state[KEEP_SESSION_ACTIVE]}
                name={KEEP_SESSION_ACTIVE}
                onChange={({ target }) => {
                  handleCheckboxChange({
                    name: target.name,
                    value: target.checked,
                  });
                }}
                // DS Checkbox locks `sx`; the row's tightness is controlled on
                // the FormControlLabel below rather than the checkbox padding.
                control={<Checkbox size="md" />}
                sx={{ my: -1 }}
              />
              <Button
                fullWidth
                variant="primary"
                type="submit"
                disabled={!isValidInfo || loading}
                loading={loading}
                onClick={handleSubmit}
                sx={{ py: 1, mt: 1 }}
              >
                Sign in
              </Button>
            </Box>
            <Box>
              <Link
                component="button"
                variant="body2"
                onClick={handleForgotPassword}
                sx={{ fontWeight: "bold" }}
              >
                Forgot your password?{" "}
              </Link>
            </Box>
            <Box mt={3}>
              <Typography variant="body-medium">
                By logging in, you agree to the{" "}
                <Link
                  href={envVariables.VITE_REACT_APP_TERM_OF_USE_URL || "/"}
                  target="_blank"
                  rel="noreferrer"
                  underline="none"
                  sx={{ fontWeight: "bold" }}
                >
                  Terms and Conditions.
                </Link>
              </Typography>
            </Box>
          </>
        )}
      </Grid>
      <Grid size={5} display="flex" alignItems="center" className="login-logo">
        <Box component="img" src={LogoWhite} alt="centro thumb" />
      </Grid>
    </LoginPage>
  );
};

export default Login;
